import React, { useState, useEffect } from 'react';
import { Sparkles, Clock, Users, Gift, TrendingUp, Zap } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AktionenSection = ({ pageType = 'diagnose' }) => {
  const [aktionen, setAktionen] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAktionen();
  }, [pageType]);

  const fetchAktionen = async () => {
    try {
      // Fetch all active aktionen for this page type (not just popup ones)
      const response = await axios.get(`${API_URL}/api/admin/aktionen`);
      const activeAktionen = response.data.filter(a => 
        a.is_active && 
        !a.show_as_popup && // Don't show popup aktionen here
        new Date(a.valid_until) > new Date()
      );
      setAktionen(activeAktionen);
    } catch (error) {
      console.error('Error fetching aktionen:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeLeft = (validUntil) => {
    const difference = new Date(validUntil) - new Date();
    
    if (difference <= 0) return null;

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return { days, hours, minutes, seconds };
  };

  const CountdownTimer = ({ validUntil }) => {
    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(validUntil));

    useEffect(() => {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft(validUntil));
      }, 1000);

      return () => clearInterval(timer);
    }, [validUntil]);

    if (!timeLeft) return <span style={{ color: '#EF4444' }}>Angebot abgelaufen!</span>;

    return (
      <div className="flex gap-2 justify-center">
        <TimeBox value={timeLeft.days} label="Tage" />
        <span className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>:</span>
        <TimeBox value={timeLeft.hours} label="Std" />
        <span className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>:</span>
        <TimeBox value={timeLeft.minutes} label="Min" />
        <span className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>:</span>
        <TimeBox value={timeLeft.seconds} label="Sek" />
      </div>
    );
  };

  const TimeBox = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div 
        className="w-16 h-16 flex items-center justify-center rounded-lg"
        style={{ 
          background: 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 100%)',
          boxShadow: '0 4px 20px rgba(124, 212, 253, 0.3)'
        }}
      >
        <span className="text-2xl font-bold" style={{ color: '#0B0F14' }}>
          {value.toString().padStart(2, '0')}
        </span>
      </div>
      <span className="text-xs mt-1" style={{ color: '#94A3B8' }}>{label}</span>
    </div>
  );

  const SpotsLeft = ({ current, max }) => {
    const remaining = max - current;
    const percentage = (remaining / max) * 100;
    
    let color = '#22C55E';
    let urgency = 'Verfügbar';
    
    if (percentage < 30) {
      color = '#EF4444';
      urgency = '🔥 Fast ausverkauft!';
    } else if (percentage < 60) {
      color = '#F59E0B';
      urgency = '⚡ Nur noch wenige!';
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold" style={{ color }}>
            {urgency}
          </span>
          <span className="text-sm font-bold" style={{ color }}>
            {remaining} / {max} verfügbar
          </span>
        </div>
        <div 
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: '#2A3142' }}
        >
          <div 
            className="h-full transition-all duration-500"
            style={{ 
              width: `${percentage}%`,
              background: `linear-gradient(90deg, ${color} 0%, ${color}AA 100%)`,
              boxShadow: `0 0 10px ${color}60`
            }}
          />
        </div>
      </div>
    );
  };

  if (loading) return null;
  if (aktionen.length === 0) return null;

  return (
    <div className="py-20" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 animate-pulse" style={{ color: '#7CD4FD' }} />
            <h2 className="text-4xl font-bold" style={{ color: '#E6EDF3' }}>
              🔥 Zeitlich begrenzte Angebote
            </h2>
            <Sparkles className="w-8 h-8 animate-pulse" style={{ color: '#7CD4FD' }} />
          </div>
          <p className="text-lg" style={{ color: '#94A3B8' }}>
            Sichern Sie sich jetzt exklusive Rabatte – bevor sie weg sind!
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {aktionen.map((aktion) => (
            <div
              key={aktion.id}
              className="relative rounded-xl p-6 transform transition-all duration-300 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #1A1F2E 0%, #2A3142 100%)',
                border: '2px solid',
                borderColor: '#7CD4FD',
                boxShadow: '0 10px 40px rgba(124, 212, 253, 0.2)'
              }}
            >
              {/* Discount Badge */}
              <div
                className="absolute -top-4 -right-4 w-20 h-20 rounded-full flex items-center justify-center transform rotate-12 animate-pulse"
                style={{
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4)'
                }}
              >
                <div className="text-center">
                  <p className="text-2xl font-bold" style={{ color: '#fff' }}>
                    -{aktion.discount_value}%
                  </p>
                </div>
              </div>

              {/* Header */}
              <div className="mb-4">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#7CD4FD' }}>
                  {aktion.name}
                </h3>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  {aktion.description}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                {aktion.first_month_free && (
                  <div 
                    className="flex items-center gap-2 p-2 rounded-lg"
                    style={{ backgroundColor: '#22C55E20' }}
                  >
                    <Gift className="w-5 h-5" style={{ color: '#22C55E' }} />
                    <span className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      Erster Monat gratis!
                    </span>
                  </div>
                )}
                
                {aktion.free_item && (
                  <div 
                    className="flex items-center gap-2 p-2 rounded-lg"
                    style={{ backgroundColor: '#7CD4FD20' }}
                  >
                    <Gift className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                    <span className="text-sm" style={{ color: '#E6EDF3' }}>
                      <strong>Gratis:</strong> {aktion.free_item} (€{aktion.free_item_value})
                    </span>
                  </div>
                )}
              </div>

              {/* Countdown Timer */}
              <div 
                className="mb-6 p-4 rounded-lg"
                style={{ backgroundColor: '#0B0F14', border: '1px solid #2A3142' }}
              >
                <div className="flex items-center gap-2 mb-3 justify-center">
                  <Clock className="w-4 h-4 animate-pulse" style={{ color: '#EF4444' }} />
                  <span className="text-xs font-semibold" style={{ color: '#EF4444' }}>
                    ⏰ ANGEBOT ENDET IN:
                  </span>
                </div>
                <CountdownTimer validUntil={aktion.valid_until} />
              </div>

              {/* Spots Left */}
              <div className="mb-6">
                <SpotsLeft 
                  current={aktion.current_customers || 0} 
                  max={aktion.max_customers} 
                />
              </div>

              {/* Coupon Code */}
              {aktion.coupon_code && (
                <div 
                  className="mb-4 p-3 rounded-lg text-center border-2 border-dashed"
                  style={{ borderColor: '#7CD4FD', backgroundColor: '#7CD4FD10' }}
                >
                  <p className="text-xs mb-1" style={{ color: '#94A3B8' }}>Gutscheincode:</p>
                  <p className="text-xl font-mono font-bold" style={{ color: '#7CD4FD' }}>
                    {aktion.coupon_code}
                  </p>
                </div>
              )}

              {/* CTA Button */}
              <Button
                onClick={() => {
                  sessionStorage.setItem('promo_code', aktion.coupon_code || '');
                  sessionStorage.setItem('promo_details', JSON.stringify(aktion));
                  window.location.href = '/signup';
                }}
                className="w-full py-6 text-lg font-bold"
                style={{
                  background: 'linear-gradient(90deg, #7CD4FD 0%, #22C55E 100%)',
                  color: '#0B0F14',
                  boxShadow: '0 4px 20px rgba(124, 212, 253, 0.3)'
                }}
              >
                <Zap className="w-5 h-5 mr-2" />
                Jetzt sichern! →
              </Button>

              {/* Trust Badge */}
              <div className="mt-4 text-center">
                <p className="text-xs" style={{ color: '#94A3B8' }}>
                  ✓ Keine Kreditkarte zum Testen erforderlich
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
